import type { ReactNode } from 'react';
import { useState, useEffect } from 'react';
import { AnimatePresence, m as motion } from 'framer-motion';
import { Card, Inline, Stack, vars } from '@etg/wings';
import { css, cx } from '@eti/styles';

interface AccordionInterface {
  id: string;
  header: ReactNode;
  body?: ReactNode;
  disabled?: boolean;
}

interface AccordionComponentProps {
  title?: ReactNode;
  footer?: ReactNode;
  className?: string;
  hasError?: boolean;
  defaultValue?: string;
  showRadioInput?: boolean;
  options?: Array<AccordionInterface>;
  onChange?: (id: string) => void;
}

interface AccordionProps extends AccordionInterface {
  hasError?: boolean;
  isExpanded?: boolean;
  showRadioInput?: boolean;
  onExpand: (accordionId: string, currentState: boolean, allowDeselect: boolean) => void;
}

const { divider, inputs, signal } = vars.colors;

const accordionChildBodyDividerClass = css`
  border-color: ${divider};
  border-top: 0.5px solid;
  width: 100%;
`;

const accordionChildBodyClass = css`
  padding: 16px 16px 8px;
`;

const inlineStyles = css`
  width: 100%;
`;

const accordionOptionStyles = (expanded: boolean, hasError: boolean) => css`
  border: 1px solid;
  border-color: ${expanded ? inputs.main : divider};
  border-radius: ${vars.card.borderRadius};

  ${hasError && `border-color: ${signal.critical.light};`}
`;

const accordionOptionHeaderStyles = (disabled?: boolean) => css`
  color: ${disabled ? inputs.border : 'inherit'};
  cursor: ${disabled ? 'default' : 'pointer'};
  padding: 15px;
`;

const styledInputRadioVariantStyles = (disabled: boolean, hasError: boolean) => css`
  accent-color: ${inputs.main};
  cursor: ${disabled ? 'default' : 'pointer'};
  height: 22px;
  width: 22px;

  &::before {
    border-radius: 50%;
    content: ' ';
    display: inline-block;
    height: 22px;
    width: 22px;
    ${hasError && `border: 1px solid ${signal.critical.light};`}
  }
`;

const AccordionChild = (props: AccordionProps) => {
  const {
    id,
    header,
    body,
    disabled = false,
    hasError = false,
    isExpanded = false,
    showRadioInput = false,
    onExpand,
  } = props;

  const onClick = (accordionId: string, currentState: boolean, allowDeselect: boolean): void => {
    if (!disabled) {
      onExpand(accordionId, currentState, allowDeselect);
    }
  };

  return (
    <div className={cx('accordion-component-option', accordionOptionStyles(isExpanded, hasError))}>
      <motion.header initial={false} onClick={() => onClick(id, isExpanded, !showRadioInput)}>
        <Stack align="start" className={accordionOptionHeaderStyles(disabled)}>
          <Inline alignY="center" className={cx('accordion-option-header', inlineStyles)}>
            {showRadioInput && (
              <input
                checked={isExpanded}
                className={cx(
                  'accordion-radio-input',
                  styledInputRadioVariantStyles(disabled, hasError),
                )}
                readOnly
                type="radio"
                value={id}
              />
            )}
            {header}
          </Inline>
        </Stack>
      </motion.header>
      <AnimatePresence initial={false}>
        {isExpanded && (
          <motion.section
            animate="open"
            className={accordionChildBodyDividerClass}
            initial="collapsed"
            key="content"
            transition={{ duration: 0.4, ease: [0.04, 0.62, 0.23, 0.98] }}
            variants={{
              open: { opacity: 1, height: 'auto' },
              collapsed: { opacity: 0, height: 0 },
            }}
          >
            {body && <div className={accordionChildBodyClass}>{body}</div>}
          </motion.section>
        )}
      </AnimatePresence>
    </div>
  );
};

const AccordionComponent = (props: AccordionComponentProps) => {
  const {
    title,
    footer,
    options,
    className,
    defaultValue = '',
    hasError = false,
    showRadioInput = false,
    onChange,
  } = props;
  const [activeAccordionId, setActiveAccordionId] = useState(defaultValue);

  useEffect(() => {
    setActiveAccordionId(defaultValue);
  }, [defaultValue]);

  const updateActiveAccordionId = (id: string): void => {
    onChange?.(id);
    setActiveAccordionId(id);
  };

  const onExpand = (accordionId: string, expandState: boolean, allowDeselect: boolean): void => {
    if (!expandState) {
      updateActiveAccordionId(accordionId);
    } else if (allowDeselect) {
      updateActiveAccordionId('');
    }
  };

  return (
    <Card>
      <Stack className={className}>
        {title && <div className="accordion-component-title">{title}</div>}
        {options?.map(({ id, header, body, disabled = false }) => {
          const isExpanded = activeAccordionId === id;
          return (
            <AccordionChild
              body={body}
              disabled={disabled}
              hasError={hasError}
              header={header}
              id={id}
              isExpanded={isExpanded}
              key={id}
              onExpand={onExpand}
              showRadioInput={showRadioInput}
            />
          );
        })}
        {footer && <div className="accordion-component-footer">{footer}</div>}
      </Stack>
    </Card>
  );
};

export type { AccordionInterface };
export { AccordionComponent };
